import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/constants/branding";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { LogIn, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/admin", { replace: true });
    } catch (err) {
      let msg;
      if (!err.response) {
        msg = "Could not reach the server. Please check your connection and try again.";
      } else if (err.response.status === 401) {
        msg = "Invalid email or password.";
      } else {
        msg = formatApiErrorDetail(err.response?.data?.detail) || "Login failed. Please try again.";
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen radial-bg flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link
          to="/"
          data-testid="admin-back-to-site"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-royal transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to website
        </Link>

        <div className="card-soft p-10">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="CLBC" className="w-12 h-12 rounded-full ring-2 ring-gold/40" />
            <div>
              <div className="text-xs uppercase tracking-widest text-gold-500">CLBC Admin</div>
              <div className="font-serif text-2xl text-royal">Sign in</div>
            </div>
          </div>
          <form className="mt-8 grid gap-5" onSubmit={onSubmit} data-testid="admin-login-form">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2"
                data-testid="admin-login-email"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2"
                data-testid="admin-login-password"
              />
            </div>
            <button disabled={busy} className="btn-primary" data-testid="admin-login-submit">
              <LogIn className="w-4 h-4" /> Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
